import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ChevronRight } from "lucide-react";
import type { DashboardAlert } from "@fiscalo/shared-types";

/**
 * Dashboard banner for a declined/lapsed subscription payment (spec 5ccc9a0e).
 *
 * The backend emits a `subscription_past_due` DashboardAlert (severity
 * warning=past_due / error=lapsed). The generic alert-bar renderer
 * (buildUnifiedAlerts in DashboardRequiresUserAction) only exists on the FREE
 * dashboard — on the paid dashboard that component is swapped for
 * Proximos60Dias, so API alerts have no renderer there. Since a past-due
 * subscription only occurs on billable (= paid) plans, the alert would never
 * show. This dedicated banner renders on BOTH dashboards, mirroring
 * PendingRecibidaAckBanner, and deep-links to the reactivate CTA in billing.
 *
 * Hidden when there is no subscription_past_due alert (the common state).
 */
export function SubscriptionPastDueBanner({
  alerts,
}: {
  alerts: DashboardAlert[];
}): JSX.Element | null {
  const { t } = useTranslation();

  const alert = useMemo(
    () => alerts.find((a) => a.type === "subscription_past_due") ?? null,
    [alerts],
  );

  if (!alert) return null;

  const lapsed = alert.severity === "error";
  const message = lapsed
    ? t(
        "dashboard.alertsBar.subscriptionLapsed",
        "Tu suscripción venció por un problema de pago. Actualizá tu forma de pago para seguir facturando.",
      )
    : t(
        "dashboard.alertsBar.subscriptionPastDue",
        "Hubo un problema con el pago de tu suscripción. Actualizá tu forma de pago para evitar interrupciones.",
      );

  // A payment problem is urgent regardless of the grace window, so both
  // past_due and lapsed render in the prominent red (fx-warn) treatment — a
  // filled background, bold copy and a solid CTA button. lapsed only deepens
  // the fill a notch.
  return (
    <div
      role="alert"
      className={`flex flex-col gap-3 rounded-[var(--fx-r-md)] border-2 border-[rgba(201,94,62,0.55)] px-5 py-4 sm:flex-row sm:items-center sm:justify-between ${
        lapsed
          ? "bg-[rgba(201,94,62,0.16)] dark:bg-[rgba(201,94,62,0.24)]"
          : "bg-[rgba(201,94,62,0.10)] dark:bg-[rgba(201,94,62,0.18)]"
      }`}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[var(--fx-warn-text)] dark:text-[#E89978]" />
        <span className="font-semibold text-[var(--fx-warn-text)] dark:text-[#E89978]">
          {message}
        </span>
      </div>
      <Link
        to={alert.actionUrl ?? "/settings?tab=billing"}
        className="inline-flex shrink-0 items-center justify-center gap-1 self-start rounded-[var(--fx-r-md)] bg-[var(--fx-warn)] px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 sm:self-auto"
      >
        {t("dashboard.alerts.cta.updatePayment", "Actualizar forma de pago")}
        <ChevronRight className="h-4 w-4" />
      </Link>
    </div>
  );
}
